import { z } from 'zod';

import { idSchema, instantSchema, paginatedSchema } from './common';
import { richTextDocumentSchema } from './work';

/** Comments ("updates"), files and the activity log of a work item (api-design §3.6, CRD-6..9). */

// ── Files (CRD-8) ────────────────────────────────────────────────────────────────────────────────

export const fileSchema = z.object({
  id: idSchema,
  taskId: idSchema,
  /** Set when the file was attached to an update. */
  commentId: idSchema.nullable(),
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive(),
  uploadedBy: idSchema,
  createdAt: instantSchema,
  /** Images and PDFs open in the browser; everything else downloads. */
  inline: z.boolean(),
  /** `GET` to view or download. */
  url: z.string(),
});

export const filesResponseSchema = z.object({ items: z.array(fileSchema) });

// ── Comments (CRD-7) ─────────────────────────────────────────────────────────────────────────────

export const commentSchema = z.object({
  id: idSchema,
  taskId: idSchema,
  authorId: idSchema,
  /** Null once deleted: the update shows "This update was deleted". */
  body: richTextDocumentSchema.nullable(),
  mentionIds: z.array(idSchema),
  files: z.array(fileSchema),
  createdAt: instantSchema,
  editedAt: instantSchema.nullable(),
  deletedAt: instantSchema.nullable(),
  permissions: z.object({ canEdit: z.boolean(), canDelete: z.boolean() }),
});

export const commentsPageSchema = paginatedSchema(commentSchema);

/**
 * ADR-0016: a UUID the phone app makes for each update and file it sends, so a repeat after a lost
 * connection returns what was stored instead of posting twice.
 */
export const clientIdSchema = idSchema;

export const createCommentRequestSchema = z.object({
  body: richTextDocumentSchema,
  /** Files uploaded to the task beforehand, now attached to this update. */
  fileIds: z.array(idSchema).max(20).default([]),
  clientId: clientIdSchema.optional(),
});

/** `POST /tasks/{id}/files?clientId=…` (multipart: the query carries it, the body the file). */
export const uploadFileQuerySchema = z.object({ clientId: clientIdSchema.optional() });

export const updateCommentRequestSchema = z.object({ body: richTextDocumentSchema });

// ── Activity log (CRD-9) ─────────────────────────────────────────────────────────────────────────

export const activityEntrySchema = z.object({
  id: idSchema,
  occurredAt: instantSchema,
  /** Null for changes made by the system. */
  actorId: idSchema.nullable(),
  /** D45: the Admin or Manager who made the change while signed in as the actor; null otherwise. */
  actedById: idSchema.nullable(),
  entityType: z.string(),
  entityId: idSchema,
  action: z.string(),
  changes: z.array(z.object({ field: z.string(), from: z.unknown(), to: z.unknown() })).nullable(),
});

export const activityPageSchema = paginatedSchema(activityEntrySchema);

export const commentRefParamsSchema = z.object({ id: idSchema });

export type TaskFile = z.infer<typeof fileSchema>;
export type Comment = z.infer<typeof commentSchema>;
export type CommentsPage = z.infer<typeof commentsPageSchema>;
export type ActivityEntry = z.infer<typeof activityEntrySchema>;
export type ActivityPage = z.infer<typeof activityPageSchema>;
export type CreateCommentRequest = z.infer<typeof createCommentRequestSchema>;
export type UploadFileQuery = z.infer<typeof uploadFileQuerySchema>;
